import type { FormField } from '../../interfaces/form.interface';
import type { InputType } from '../../interfaces/input.interface';
import { InputType as ReactiveInputType } from '../../interfaces/input.interface';
import {
    ReactiveTextFieldFactory,
    ReactiveTextareaFieldFactory,
    type ReactiveFieldConditionsFactoryConfig,
    type ReactiveFieldExtraConfig,
} from '../factory/reactive-field-factory';

const DEFAULT_MODAL_READONLY_WIDTH = 3;

export interface ReactiveModalReadonlyFieldFactoryConfig<TModel> {
    readonly name: string;
    readonly label: string;
    readonly order: number;
    readonly width?: number;
    readonly type?: InputType;
    readonly visible?: boolean;
    readonly isEditable?: boolean;
    readonly conditions?: ReactiveFieldConditionsFactoryConfig;
    readonly extra?: ReactiveFieldExtraConfig<TModel>;
}

/** Builds legacy-compatible display fields used by read-only/list modals. */
export class ReactiveModalReadonlyFieldFactory {
    public static create<TModel>(config: ReactiveModalReadonlyFieldFactoryConfig<TModel>): FormField<TModel> {
        if (config.type === ReactiveInputType.TEXTAREA) {
            return ReactiveTextareaFieldFactory.create<TModel>({
                name: config.name,
                label: config.label,
                order: config.order,
                width: config.width ?? DEFAULT_MODAL_READONLY_WIDTH,
                isEditable: config.isEditable ?? true,
                conditions: this.resolveConditions(config),
                textAreaConfiguration: {
                    hasVoiceTranscription: false,
                    canTakePhoto: false,
                    photoField: undefined,
                },
                ...(config.extra !== undefined ? { extra: config.extra } : {}),
            });
        }

        return ReactiveTextFieldFactory.create<TModel>({
            name: config.name,
            label: config.label,
            order: config.order,
            width: config.width ?? DEFAULT_MODAL_READONLY_WIDTH,
            isEditable: config.isEditable ?? true,
            conditions: this.resolveConditions(config),
            ...(config.extra !== undefined ? { extra: config.extra } : {}),
        });
    }

    public static createMany<TModel>(configs: readonly ReactiveModalReadonlyFieldFactoryConfig<TModel>[]): FormField<TModel>[] {
        return configs.map((config) => this.create(config));
    }

    private static resolveConditions<TModel>(
        config: ReactiveModalReadonlyFieldFactoryConfig<TModel>,
    ): ReactiveFieldConditionsFactoryConfig {
        return {
            rememberedOnDuplicate: true,
            required: false,
            retained: false,
            undoEnabled: false,
            visible: config.visible ?? true,
            ...(config.conditions ?? {}),
        };
    }
}
